import React, { useState, useEffect, useRef } from "react";
import SectionHeader from "@src/components/widgets/SectionHeader/SectionHeader";
import { useParams } from "react-router-dom";
import * as ProductApi from "@src/api/ProductApi";
import ProductCategoryDisplay from "@src/components/widgets/ProductCategoryDisplay/ProductCategoryDisplay";
import {
  Dropdown,
  PaginationControls,
  GridContainer,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import filter from "lodash/filter";
import pick from "lodash/pick";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import { Product } from "@src/types/product";
import PxProductTile from "@src/components/widgets/PxProductTile";

const SORT_BY_ALPHABETICAL = "abc";
const SORT_BY_PRICE = "price";
const SORT_BY_LABELS = {
  [SORT_BY_ALPHABETICAL]: "Alphabetical",
  [SORT_BY_PRICE]: "Price",
} as const;

type SortByOption = typeof SORT_BY_ALPHABETICAL | typeof SORT_BY_PRICE | null;

interface PaginationFilters {
  pageSize: number;
  page: number;
  totalPages: number;
  totalCount: number;
}

interface Filters {
  default?: boolean;
  [key: string]: any;
}

interface MenuController {
  setIsOpen: (isOpen: boolean) => void;
}

interface RouteParams extends Record<string, string | undefined> {
  productCategoryId: string;
}

function ProductListPage() {
  const { showToast } = useToast();
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [paginationFilters, setPaginationFilters] = useState<PaginationFilters>(
    {
      pageSize: 20,
      page: 1,
      totalPages: 0,
      totalCount: 0,
    },
  );
  const [filters, setFilters] = useState<Filters>({});
  const [sortBy, setSortBy] = useState<SortByOption>(null);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [filterMenuController, setFilterMenuController] =
    useState<MenuController | null>(null);
  const { productCategoryId } = useParams<RouteParams>();
  const pageContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    handleLoadData();
  }, [
    productCategoryId,
    sortBy,
    paginationFilters.pageSize,
    paginationFilters.page,
  ]);

  const handleLoadData = async () => {
    setIsLoadingProducts(true);
    try {
      const combinedFilters = {
        ...pick(paginationFilters, ["page", "pageSize"]),
        ...filters,
        sortBy: sortBy,
      };
      const response = await ProductApi.getProductCategoryProducts(
        productCategoryId!,
        combinedFilters,
      );
      const { results, ...updatedPaginationFilters } = response.body;
      setProducts(results);
      setPaginationFilters({
        ...paginationFilters,
        ...updatedPaginationFilters,
      });

      const activeFilters = filter(filters, (f) => !!f);
      setActiveFilterCount(Object.keys(activeFilters).length);

      document.body.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error loading products",
        variant: "error",
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleFilterChange = ({
    name,
    value,
  }: {
    name: string;
    value: any;
  }) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleApplyFilters = () => {
    filterMenuController?.setIsOpen(false);
    handleLoadData();
  };

  const handlePaginationFilterChanged = ({
    name,
    value,
  }: {
    name: string;
    value: number;
  }) => {
    const updatedPaginationFilters = {
      ...paginationFilters,
      [name]: value,
    };
    if (name === "pageSize") {
      updatedPaginationFilters.page = 1;
    }
    setPaginationFilters(updatedPaginationFilters);
  };

  return (
    <div ref={pageContentRef}>
      {isLoadingProducts && <SpinningLoader />}
      <SectionHeader
        header={
          <span className="text-xl">
            <ProductCategoryDisplay productCategoryId={productCategoryId!} />
          </span>
        }
        rightSideContent={
          <div className="flex-row spaced-content">
            {/* Note: Commented out for Beta due to missing functionality */}
            {/* <Menu
              buttonContent={(
                <Button
                  className="btn btn-dropdown"
                  variant="pagination"
                  icon={<i className="fa-regular fa-filter btn-dropdown-icon font-weight-normal" />}
                  iconPosition="left"
                  onClick={() => {}}
                >
                  <div className="flex-row flex-start flex-center spaced-content">
                    <span>Filter</span>
                    {activeFilterCount > 0 && (
                      <NumberBubble value={activeFilterCount} />
                    )}
                  </div>
                </Button>
              )}
              onReceiveController={setFilterMenuController}
            >
              <div className="padding-xxl">
                <div>
                  <h4 className="text-sm">Filter Category</h4>
                  <Checkbox
                    className="text-sm"
                    label="Default Checkmark"
                    name="default"
                    checked={filters.default || false}
                    onChange={(checked: boolean) =>
                      handleFilterChange({
                        name: "default",
                        value: checked,
                      })
                    }
                  />
                </div>

                <div className="margin-top-lg">
                  <Button
                    label="Apply Filters"
                    onClick={handleApplyFilters}
                  />
                </div>
              </div>
            </Menu> */}
            <Dropdown
              className="dropdown"
              items={[
                {
                  label: "Default",
                  value: null,
                },
                {
                  label: SORT_BY_LABELS[SORT_BY_ALPHABETICAL],
                  value: SORT_BY_ALPHABETICAL,
                },
                {
                  label: SORT_BY_LABELS[SORT_BY_PRICE],
                  value: SORT_BY_PRICE,
                },
              ]}
              label="Sort by"
              onItemClick={({ value }: { value: any }) =>
                setSortBy(value as SortByOption)
              }
              selectedLabel={sortBy ? SORT_BY_LABELS[sortBy] : undefined}
              size={150}
              dropdownIcon={
                <i className="fa-sharp fa-regular fa-chevron-down" />
              }
            />
          </div>
        }
      />
      <div className="padding-top-xxl">
        <GridContainer columns={{ base: 5 }} gap="var(--margin-sm)">
          {products.map((product, index) => (
            <div
              key={`product-list-row-product-${product.id ?? index}-${index}`}
            >
              <PxProductTile
                product={product}
                className="width-100-important"
              />
            </div>
          ))}
        </GridContainer>

        <div className="padding-top-xxl">
          <PaginationControls
            onChange={handlePaginationFilterChanged}
            values={paginationFilters}
            data-testid="product-list-page-pagination-controls"
          />
        </div>
      </div>
    </div>
  );
}

export default ProductListPage;
